import { PolymerElement, html } from '@polymer/polymer/polymer-element.js';
import '../../material-icon/material-icon.js';
import '@polymer/paper-tooltip/paper-tooltip.js';
import { ToArray } from '../../../modules/ToArray.js';

class Stopwatch extends PolymerElement {
  static get template() {
    return html`
      <style>
        :host {
          display: flex;
          position: relative;
          align-items: center;
          user-select: none;
          -webkit-user-select: none; /* Safari */
        }
        :host([is-on]) material-icon#timer {
          color: var(--icon-color, #ff8300);
        }
        :host(:not([disabled])) material-icon:hover {
          opacity: 0.6;
          cursor: pointer;
        }
        :host([disabled]) paper-cell[icon] {
          display: none;
        }
        paper-tooltip {
          --paper-tooltip-duration-in: 100;
        }
        material-icon {
          user-select: none;
          -webkit-user-select: none; /* Safari */
        }
      </style>
      <katapult-firebase-worker id="timeBuckets" path="[[path]]/time_bucket" data="{{timeBuckets}}"></katapult-firebase-worker>
      <paper-cell shrink>
        <p>[[timeString]]</p>
      </paper-cell>
      <paper-cell icon>
        <material-icon id="timer" icon="timer" size="medium" access-key="s" on-click="toggleWatch"></material-icon>
      </paper-cell>
      <paper-cell icon>
        <material-icon id="reset" icon="refresh" size="medium" on-click="resetWatch"></material-icon>
      </paper-cell>
      <paper-tooltip for="timer">Click to start/stop the stopwatch.</paper-tooltip>
      <paper-tooltip for="reset">Click to reset the stopwatch.</paper-tooltip>
    `;
  }

  static get properties() {
    return {
      isOn: {
        type: Boolean,
        value: false,
        notify: true,
        reflectToAttribute: true
      },
      disabled: {
        type: Boolean,
        value: false,
        reflectToAttribute: true,
        observer: '_isDisabled'
      },
      time: {
        type: Number,
        value: 0
      },
      timeString: {
        type: String,
        value: ''
      },
      interval: {
        type: Number,
        value: null
      },
      offset: {
        type: Number,
        value: Date.now()
      },
      path: {
        type: String
      },
      timeElapsed: {
        type: Number
      },
      timeBuckets: {
        type: Object
      },
      timeBucketsArray: {
        type: Array,
        value: () => []
      }
    };
  }

  static get observers() {
    return ['_updateTimeBucketsArray(timeBuckets.*)', '_handleTimeBuckets(timeBucketsArray.*)'];
  }

  constructor() {
    super();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.interval);
  }

  _updateTimeBucketsArray() {
    this.timeBucketsArray = ToArray(this.timeBuckets);
  }

  _handleTimeBuckets() {
    this.timeString = this.formatTime(this.elapsedTime());
    let item = this.timeBucketsArray.slice(-1)[0];
    if (this.timeBucketsArray.length) {
      if (item.start_time) {
        if (!item.stop_time) {
          //incomplete bucket
          this.isOn = true;
          this.key = item.$key;
          this.time = this.elapsedTime();
          this.timeString = this.formatTime(this.elapsedTime());
          this.startWatch();
        } else {
          // complete bucket
          this.isOn = false;
        }
      }
    }
    this.time = this.elapsedTime();
    this.timeString = this.formatTime(this.elapsedTime());
  }

  _isDisabled(yes) {
    if (yes) {
      if (this.isOn) {
        this.stopWatch();
        this.isOn = false;
      }
    }
  }

  toggleWatch() {
    this.isOn = !this.isOn;
    if (this.isOn) {
      this.updateThisKey();
      this.startWatch();
    } else {
      this.stopWatch();
    }
  }

  // either starts a new time bucket or saves key of last incomplete bucket
  updateThisKey() {
    if (!this.timeBucketsArray.length) {
      // no buckets
      this.key = this.$.timeBuckets._ref.push({ start_time: Date.now() }).key;
    } else {
      // check out most recent time bucket
      let item = this.timeBucketsArray.slice(-1)[0];
      if (item.start_time) {
        if (!item.stop_time) {
          //incomplete bucket
          this.key = item.$key;
        } else {
          // complete bucket
          this.key = this.$.timeBuckets._ref.push({ start_time: Date.now() }).key;
        }
      }
    }
  }

  // return total time from all saved buckets
  elapsedTime() {
    let time_elapsed = 0;
    this.timeBucketsArray.forEach((bucket) => {
      if (bucket.stop_time) {
        time_elapsed += bucket.stop_time - bucket.start_time;
      } else {
        time_elapsed += Date.now() - bucket.start_time;
      }
    });
    return time_elapsed;
  }

  // updating timeString
  update() {
    if (this.isOn) {
      this.time += this.delta();
    }
    this.timeString = this.formatTime(this.time);
  }

  formatTime(rawTime) {
    rawTime = rawTime.toString().slice(0, -3);

    // rearrange time
    let hr = Math.floor(rawTime / 3600).toString();
    hr = hr.length < 2 ? '0' + hr : hr;

    let min = Math.floor((rawTime % 3600) / 60).toString();
    min = min.length < 2 ? '0' + min : min;

    let sec = ((rawTime % 3600) % 60).toString();
    sec = sec.length < 2 ? '0' + sec : sec;

    rawTime = hr + min + sec;

    // format time
    switch (rawTime.length) {
      case 0:
        rawTime = '00:00:00';
        break;
      case 2:
        rawTime = '00:00:' + rawTime;
        break;
      case 4:
        rawTime = '00:' + rawTime.slice(0, 2) + ':' + rawTime.slice(2);
        break;
      case 6:
        rawTime = rawTime.slice(0, 2) + ':' + rawTime.slice(2, 4) + ':' + rawTime.slice(4);
        break;
    }
    return rawTime;
  }

  // return the time passed
  delta() {
    let now = Date.now();
    let timePassed = now - this.offset;
    this.offset = now;
    return timePassed;
  }

  startWatch() {
    this.offset = Date.now();
    this.interval = setInterval(this.update.bind(this), 10);
  }

  stopWatch() {
    FirebaseWorker.ref(`${this.path}/time_bucket/${this.key}/stop_time`).set(Date.now());
    clearInterval(this.interval);
    this.interval = null;
  }

  async resetWatch() {
    //delete timebuckets
    FirebaseWorker.ref(`${this.path}/time_bucket`).remove();
    if (this.isOn) {
      this.key = FirebaseWorker.ref(`${this.path}/time_bucket`).push({ start_time: Date.now() }).key;
    }
    this.offset = Date.now();
    this.time = 0;
    this.interval = null;
  }
}
window.customElements.define('stop-watch', Stopwatch);
